import type { DeleteTestDlnsRequest, DeleteTestDlnsResponse } from './types';

import { useMutation } from '@tanstack/react-query';

import ToastService from '~/services/toast.service';

import { i18n } from '~/utils';

import { deleteTestDlns } from './deleteTestDlns';

/**
 * TanStack Query mutation hook for deleting test DLNs
 *
 * This hook provides a mutation for deleting all test DLNs of a buyer company.
 * Test DLNs are identified based on their DLN number (starts with "test-").
 *
 * The mutation supports both dry run (count only) and actual deletion modes.
 *
 * @returns TanStack Query mutation object
 *
 * @example
 * ```tsx
 * const deleteTestDlnsMutation = useMutationDeleteTestDlns();
 *
 * // Dry run to count affected DLNs
 * const handleDryRun = () => {
 *   deleteTestDlnsMutation.mutate({
 *     buyerId: companyId,
 *     dryRun: true,
 *   });
 * };
 *
 * // Actually delete test DLNs
 * const handleDelete = () => {
 *   deleteTestDlnsMutation.mutate({
 *     buyerId: companyId,
 *     dryRun: false,
 *   });
 * };
 * ```
 */
export const useMutationDeleteTestDlns = () => {
  return useMutation<DeleteTestDlnsResponse, Error, DeleteTestDlnsRequest>({
    mutationFn: deleteTestDlns,
    onSuccess(data, variables) {
      if (variables.dryRun) {
        return;
      }

      const successMessage = i18n.t(($) => $.delivery.deleteTestDlns.success, {
        ns: 'data',
        count: data.countMains,
      });

      ToastService.success(successMessage);
    },
  });
};
